import React, { FC, useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MATCH_TYPE, SearchResultItem, SHARED_SECTION } from 'models/Search';
import ReorderResizeTable from 'components/ReorderResizeTable';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import ArrowReplaceIcon from 'icons/ArrowReplace.icon';
import { getArrayLength, getIsArrayEmpty } from 'utils';
import {
  BEST_MATCH_COLUMNS,
  COLUMNS,
  SHARED_TABLE_PROPS,
  SKELETON_ROWS,
  getBodyContentGenerate,
} from './data';
import { getDealNavigationUrl, splitDealsByMatchType } from './utils';
import { EMPTY_ARRAY } from 'data/global';

import './SearchResultsTable.styles.scss';

type SearchResultsTableProps = {
  className?: string;
  deals: SearchResultItem[];
  type: string;
  searchString?: string;
  isLoading?: boolean;
};

type SearchResultsRow = SearchResultItem & { isDivider?: boolean };

const DIVIDER_ROW: SearchResultsRow = { isDivider: true };

const SearchResultsTable: FC<SearchResultsTableProps> = (props) => {
  const {
    className,
    type,
    searchString,
    isLoading,
    deals = EMPTY_ARRAY,
  } = props;

  const { isSuperAdmin } = useContext(CurrentUserContext);

  const { t } = useTranslation();

  const navigate = useNavigate();

  const classes = classNames('search-results-table', className);

  const columns =
    type === SHARED_SECTION.BEST_MATCH ? BEST_MATCH_COLUMNS : COLUMNS;

  const { exactDeals, approximateDeals } = useMemo(
    () => splitDealsByMatchType(deals),
    [deals],
  );

  const hasApproximateDeals = !getIsArrayEmpty(approximateDeals);

  const tableData = useMemo(
    () =>
      hasApproximateDeals
        ? [...exactDeals, DIVIDER_ROW, ...approximateDeals]
        : deals,
    [hasApproximateDeals, exactDeals, approximateDeals, deals],
  );

  const tableContentTransformations = useMemo(() => {
    const bodyContentGenerate = getBodyContentGenerate();

    return columns.reduce(
      (acc, col) => ({
        ...acc,
        [col]: (result: SearchResultsRow) => {
          if (!result.isDivider)
            return bodyContentGenerate[col]({
              deal: result,
              type,
            });

          if (col !== columns[0]) return null;

          return (
            <div className="search-results-table__divider">
              <ArrowReplaceIcon />
              <span>{t('similarTo', { searchString })}</span>
            </div>
          );
        },
      }),
      {},
    );
  }, [columns, type, searchString, t]);

  const getColSpan = useCallback(
    (result: SearchResultsRow) =>
      result.isDivider ? getArrayLength(columns) : 1,
    [columns],
  );

  const getRowBackgroundColor = useCallback((result: SearchResultsRow) => {
    if (result?.isDivider) return 'search-results-table__divider-row';
    if (result?.matchType === MATCH_TYPE.APPROXIMATE)
      return 'search-results-table__approximate-row';

    return undefined;
  }, []);

  const onRowClick = useCallback(
    (result: SearchResultsRow) => {
      if (result.isDivider) return;

      navigate(
        getDealNavigationUrl(result, result.section ?? type, isSuperAdmin),
      );
    },
    [type, isSuperAdmin, navigate],
  );

  return (
    <ReorderResizeTable
      className={classes}
      data={tableData}
      isDataLoading={isLoading}
      numberOfPlaceholderRows={SKELETON_ROWS}
      tableContentTransformations={tableContentTransformations}
      onRowClick={onRowClick}
      getColSpan={getColSpan}
      getRowBackgroundColor={getRowBackgroundColor}
      {...SHARED_TABLE_PROPS}
      columns={columns}
    />
  );
};

export default SearchResultsTable;
